import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Bell, Palette, Globe, Plug, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuthV2";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ServiceIntegration } from "@/types/api-v2";
import { aiRuntimeApi } from "@/services/api-v2.service";
import { useTheme } from "next-themes";
import { USER_SETTINGS_STORAGE_KEY } from "@/lib/user-settings-storage";
import { SecuritySettingsPanel } from "@/components/security/SecuritySettingsPanel";

const settingsSchema = z.object({
  // Уведомления
  emailNotifications: z.boolean(),
  pushNotifications: z.boolean(),
  systemAlerts: z.boolean(),
  
  // Безопасность
  twoFactorEnabled: z.boolean(),
  sessionTimeout: z.string(),
  
  // Интерфейс
  theme: z.enum(["light", "dark", "system"]),
  language: z.string(),
  mapDefaultZoom: z.string(),
  autoSave: z.boolean(),
  
  // Приватность
  profileVisibility: z.string(),
  dataSharing: z.boolean(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

const DEFAULT_SETTINGS: SettingsFormData = {
  emailNotifications: true,
  pushNotifications: false,
  systemAlerts: true,
  twoFactorEnabled: false,
  sessionTimeout: "24",
  theme: "system",
  language: "ru",
  mapDefaultZoom: "10",
  autoSave: true,
  profileVisibility: "private",
  dataSharing: false,
};

// Компонент для отображения интеграций сервисов
function ServiceIntegrationsSection() {
  const { data: activeIntegrations = [], isLoading } = useQuery<ServiceIntegration[]>({
    queryKey: ['/api/v1/service-integrations/active'],
  });

  const { data: aiRuntime, isLoading: aiLoading } = useQuery({
    queryKey: ['ai', 'runtime-availability'],
    queryFn: () => aiRuntimeApi.getAvailability(),
  });

  const aiIntegrations = activeIntegrations.filter(
    (integration) => integration.serviceType === 'ai'
  );

  if (isLoading || aiLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (aiIntegrations.length === 0 && !aiRuntime?.usable) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="mb-2">ИИ-ассистент недоступен</p>
        <p className="text-sm">
          Нет настроенного LLM в разделе админки «ИИ-агенты» (схема ai.llm_providers). Обратитесь к администратору.
        </p>
      </div>
    );
  }

  if (aiIntegrations.length === 0 && aiRuntime?.usable) {
    return (
      <div className="text-center py-8 text-gray-600 space-y-2">
        <p className="font-medium text-green-700">ИИ доступен через настройку LLM в админке</p>
        <p className="text-sm">
          Провайдер настроен в схеме <code className="text-xs bg-muted px-1 rounded">ai</code> (вкладка «ИИ-агенты» в админке).
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {aiIntegrations.map((integration) => {
        const settings = (integration.settings || {}) as Record<string, unknown>;
        const providerName = integration.name;
        
        return (
          <div
            key={integration.id}
            className="flex items-center justify-between rounded-lg border p-4"
          >
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                {integration.isActive ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div>
                <div className="font-medium">{providerName}</div>
                <div className="text-sm text-gray-500">
                  Модель: {String(settings?.model ?? 'не указана')}
                </div>
                {!!settings?.provider && (
                  <div className="text-xs text-gray-400 mt-1">
                    Провайдер: {String(settings.provider ?? '')}
                  </div>
                )}
              </div>
            </div>
            <div className="text-sm">
              {integration.isActive ? (
                <span className="text-green-600 font-medium">Активна</span>
              ) : (
                <span className="text-gray-400">Неактивна</span>
              )}
            </div>
          </div>
        );
      })}
      <div className="text-xs text-gray-500 mt-4 pt-4 border-t">
        <p>
          Для управления интеграциями обратитесь к администратору системы.
          Настройка интеграций доступна в административной панели.
        </p>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { isLoading } = useAuth();
  const { toast } = useToast();
  const { setTheme } = useTheme();

  const storedSettings = useMemo(() => {
    if (typeof window === "undefined") return DEFAULT_SETTINGS;
    try {
      const raw = localStorage.getItem(USER_SETTINGS_STORAGE_KEY);
      if (!raw) return DEFAULT_SETTINGS;
      const parsed = JSON.parse(raw) as Partial<SettingsFormData>;
      const merged = { ...DEFAULT_SETTINGS, ...parsed };
      if (!["light", "dark", "system"].includes(merged.theme)) {
        merged.theme = DEFAULT_SETTINGS.theme;
      }
      return merged;
    } catch {
      return DEFAULT_SETTINGS;
    }
  }, []);

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: storedSettings,
  });

  React.useEffect(() => {
    form.reset(storedSettings);
  }, [storedSettings, form]);

  // Обновление настроек
  const updateSettingsMutation = useMutation({
    mutationFn: async (data: SettingsFormData) => {
      if (typeof window !== "undefined") {
        localStorage.setItem(USER_SETTINGS_STORAGE_KEY, JSON.stringify(data));
      }
      return data;
    },
    onSuccess: (data) => {
      setTheme(data.theme);
      toast({
        title: "Настройки сохранены",
        description: "Ваши настройки успешно обновлены",
      });
    },
    onError: () => {
      toast({
        title: "Ошибка",
        description: "Не удалось сохранить настройки",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SettingsFormData) => {
    updateSettingsMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-6">
        {/* Навигация */}
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Вернуться на главную
            </Button>
          </Link>
          <h1 className="text-3xl font-bold text-foreground">Настройки</h1>
          <p className="text-muted-foreground mt-1">Управление настройками вашего аккаунта</p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Уведомления */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Bell className="mr-2 h-5 w-5" />
                    Уведомления
                  </CardTitle>
                  <CardDescription>
                    Настройте способы получения уведомлений
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="emailNotifications"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Email уведомления</FormLabel>
                          <FormDescription>
                            Получать уведомления на электронную почту
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="pushNotifications"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Push уведомления</FormLabel>
                          <FormDescription>
                            Получать уведомления в браузере
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="systemAlerts"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Системные уведомления</FormLabel>
                          <FormDescription>
                            Важные системные сообщения и обновления
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <SecuritySettingsPanel />

              {/* Интерфейс */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Palette className="mr-2 h-5 w-5" />
                    Интерфейс
                  </CardTitle>
                  <CardDescription>
                    Настройки отображения и интерфейса
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="theme"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Тема оформления</FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Выберите тему" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="light">Светлая</SelectItem>
                            <SelectItem value="dark">Темная</SelectItem>
                            <SelectItem value="system">Системная</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="language"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Язык интерфейса</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Выберите язык" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="ru">Русский</SelectItem>
                            <SelectItem value="en">English</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mapDefaultZoom"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Масштаб карты по умолчанию</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Выберите масштаб" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="8">Область</SelectItem>
                            <SelectItem value="10">Город</SelectItem>
                            <SelectItem value="12">Район</SelectItem>
                            <SelectItem value="15">Улица</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="autoSave"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Автосохранение</FormLabel>
                          <FormDescription>
                            Автоматически сохранять изменения
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Приватность */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Globe className="mr-2 h-5 w-5" />
                    Приватность
                  </CardTitle>
                  <CardDescription>
                    Управление приватностью данных
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="profileVisibility"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Видимость профиля</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Выберите уровень" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="public">Публичный</SelectItem>
                            <SelectItem value="organization">Только орган управления</SelectItem>
                            <SelectItem value="private">Приватный</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Кто может видеть ваш профиль
                        </FormDescription>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dataSharing"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel>Обмен данными</FormLabel>
                          <FormDescription>
                            Разрешить обмен анонимными данными для улучшения сервиса
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Интеграции сервисов */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Plug className="mr-2 h-5 w-5" />
                  Интеграции сервисов
                </CardTitle>
                <CardDescription>
                  Управление интеграциями ИИ ассистента
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ServiceIntegrationsSection />
              </CardContent>
            </Card>

            {/* Кнопка сохранения */}
            <div className="flex justify-end pt-6">
              <Button 
                type="submit" 
                disabled={updateSettingsMutation.isPending}
                className="min-w-[120px]"
              >
                {updateSettingsMutation.isPending ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Сохранить
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}